import { Injectable } from '@angular/core';
import {
  API_export_pdf_detailed,
  API_export_pdf_detailed_overview,
  API_export_pdf_individual,
  API_export_pdf_individual_zip,
  API_export_pdf_simple,
  API_export_pdf_simple_overview,
  API_export_pdf_site_plan,
  API_export_word_detailed,
  API_export_word_individual,
  API_export_word_individual_zip,
  API_export_word_simple_overview,
} from '@core/api/paths';
import { Observable, of, throwError } from 'rxjs';
import { tap } from 'rxjs/operators';
import { EStatusCode } from 'src/app/core/helpers/error-codes';
import { PromptService } from '../../components/prompt/prompt.service';
import { TranslationPipe } from '../../features/translate/translation.pipe';
import { DownloadService } from '../../services/download.service';
import { TSearchFilterDTO } from '../filters-advanced/models/advanced-filter.model';
import { QuickFilterService } from '../filters/filters-api/search/quick-filter-service';
import { QuickSortService } from '../filters/filters-api/sort/quick-sort.service';
import {
  EPointSearchOperator,
  TSearchAdvancedFilterOperator,
  TSearchFilterSubgroup,
} from '../points/point-search/point-search.consts';
import { PreferencesService } from '../preferences/preferences-service/preferences.service';
import { GET_TABLE } from '../site/site-table/table.ui.store';
import { WorkspaceService } from '../workspace/workspace.service';
import {
  TElementPerWorkspace,
  TExportBody,
  TExportBodyRefactored,
  TSectionVisibilitySettings,
} from './export-body.model';
import { PointExportLocalService } from './point-export-local.service';
import { PointExportPreferencesService } from './point-export-preferences.service';
import { PointExportUtilsService } from './point-export-utils.service';
import { PointExportVisibilitySettingsService } from './point-export-visibility-settings.service';
import { TExportData } from './utils/export-data';
import { EExportFormat } from './utils/export-page-format-enum';
import { EExportTypeName } from './utils/export-type-names-enum';
import { EAdvancedFilterType } from '../filters-advanced/models/advanced-filter-type.enum';

// TODO Delete once the backend no longer requires exportFilter
const DEPRECATED_EXPORT_FILTER: TExportBody['exportFilter'] = {
  priority: '',
  status: '',
  orderedBy: '',
  tags: '',
  excludedTags: '',
};

export type TExportFunctionParams = {
  workspaceId: string;
  pointIds: string[] | TElementPerWorkspace[];
  customFieldIds: string[];
  exportData: TExportData;
  coords: string[];
  exportType: EExportTypeName;
  isOverview: boolean;
  isPdf: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class PointExportService {
  constructor(
    private promptService: PromptService,
    private translationPipe: TranslationPipe,
    private preferencesService: PreferencesService,
    private pointExportVisibilitySettingsService: PointExportVisibilitySettingsService,
    private downloadService: DownloadService,
    private pointExportPreferencesService: PointExportPreferencesService,
    private pointExportLocalService: PointExportLocalService,
    private pointExportUtilsService: PointExportUtilsService,
    private quickFilterService: QuickFilterService,
    private quickSortService: QuickSortService,
    private workspaceService: WorkspaceService,
  ) {}

  export({
    workspaceId,
    pointIds,
    customFieldIds,
    exportData,
    coords,
    exportType,
    isOverview,
    isPdf,
  }: TExportFunctionParams): Observable<Response> {
    if (pointIds?.length === 0) {
      this.showPermissionDeniedWarninig();

      return throwError('');
    }

    const mapData = this.pointExportUtilsService.pushPlan();
    const format: EExportFormat =
      exportType === EExportTypeName.ZIP || exportType === EExportTypeName.ZIP_WORD
        ? EExportFormat.A4
        : exportData.format;

    const sectionsVisibilitySettings: TSectionVisibilitySettings =
      this.pointExportVisibilitySettingsService.getSectionVisibilitySettings(
        exportType,
        exportData,
        customFieldIds,
      );

    const elementsPerWorkspace = isOverview
      ? this.pointExportUtilsService.getOverviewElementsPerWorkspace(pointIds)
      : this.pointExportUtilsService.getElementsPerWorkspace(workspaceId, coords, pointIds);

    const body: TExportBody = {
      timeGMT: exportData.timeZone,
      format,
      mapZoom: mapData.mapZoomString,
      elementsPerWorkspace,
      sectionsVisibilitySettings,
      exportFilter: DEPRECATED_EXPORT_FILTER,
    };

    switch (exportType) {
      case EExportTypeName.CSV:
        this.pointExportLocalService.exportLocally('csv');

        return of(null);
      case EExportTypeName.XLSX:
        this.pointExportLocalService.exportLocally('xlsx');

        return of(null);
      case EExportTypeName.DETAILED_LIST:
        if (isOverview) {
          return this.exportOverviewDetailedList(sectionsVisibilitySettings, body);
        }

        return this.exportDetailedList(workspaceId, sectionsVisibilitySettings, exportData, body);
      case EExportTypeName.PDF:
        return this.exportIndividualPdf(workspaceId, sectionsVisibilitySettings, exportData, body);
      case EExportTypeName.SIMPLE_LIST:
        if (isOverview) {
          return this.exportOverviewSimpleList(body);
        } else {
          return this.exportSimpleList(workspaceId, body);
        }

      case EExportTypeName.SITE_PLAN:
        return this.exportFromUrl(API_export_pdf_site_plan(), body);
      case EExportTypeName.WORD:
        return this.exportWord(workspaceId, sectionsVisibilitySettings, body);

      case EExportTypeName.INDIVIDUAL_WORD:
        return this.exportIndividualWord(workspaceId, sectionsVisibilitySettings, body);
      case EExportTypeName.WORD_SIMPLE:
        return this.exportSimpleWord(body);
      case EExportTypeName.ZIP:
        return this.exportZip(isPdf, workspaceId, sectionsVisibilitySettings, body);
      case EExportTypeName.ZIP_WORD:
        return this.exportZipWord(workspaceId, sectionsVisibilitySettings, body);
    }
  }

  private exportZipWord(
    workspaceId: string,
    sectionsVisibilitySettings: TSectionVisibilitySettings,
    body: TExportBody,
  ): Observable<Response> {
    this.pointExportPreferencesService.updatePreferences({
      workspaceId,
      exportType: 'word',
      sectionsVisibilitySettings,
      format: null,
    });

    return this.exportFromUrl(API_export_word_individual_zip(), body);
  }

  private exportZip(
    isPdf: boolean,
    workspaceId: string,
    sectionsVisibilitySettings: TSectionVisibilitySettings,
    body: TExportBody,
  ): Observable<Response> {
    if (isPdf) {
      this.pointExportPreferencesService.updatePreferences({
        workspaceId,
        exportType: 'pdf',
        sectionsVisibilitySettings,
      });
    } else {
      this.pointExportPreferencesService.updatePreferences({
        workspaceId,
        exportType: 'zip',
        sectionsVisibilitySettings,
      });
    }

    return this.exportFromUrl(API_export_pdf_individual_zip(), body);
  }

  private exportSimpleWord(body: TExportBody): Observable<Response> {
    this.pointExportPreferencesService.updateOverviewPreferences({
      exportType: 'word',
      sectionsVisibilitySettings: body.sectionsVisibilitySettings,
      format: body.format,
    });

    return this.exportFromUrl(API_export_word_simple_overview(), this.formatWithFilters(body));
  }

  private exportIndividualWord(
    workspaceId: string,
    sectionsVisibilitySettings: TSectionVisibilitySettings,
    body: TExportBody,
  ): Observable<Response> {
    this.pointExportPreferencesService.updatePreferences({
      workspaceId,
      exportType: 'word',
      sectionsVisibilitySettings,
    });

    return this.exportFromUrl(API_export_word_individual(), body);
  }

  private exportWord(
    workspaceId: string,
    sectionsVisibilitySettings: TSectionVisibilitySettings,
    body: TExportBody,
  ): Observable<Response> {
    this.pointExportPreferencesService.updatePreferences({
      workspaceId,
      exportType: 'word',
      sectionsVisibilitySettings,
    });

    return this.exportFromUrl(API_export_word_detailed(), body);
  }

  private exportSimpleList(workspaceId: string, body: TExportBody): Observable<Response> {
    this.pointExportPreferencesService.updatePreferences({
      workspaceId,
      exportType: 'simple',
      sectionsVisibilitySettings: body.sectionsVisibilitySettings,
      format: body.format,
    });

    return this.exportFromUrl(API_export_pdf_simple(), body);
  }

  private exportOverviewSimpleList(body: TExportBody): Observable<Response> {
    this.pointExportPreferencesService.updateOverviewPreferences({
      exportType: 'simple',
      sectionsVisibilitySettings: body.sectionsVisibilitySettings,
      format: body.format,
    });

    return this.exportFromUrl(API_export_pdf_simple_overview(), this.formatWithFilters(body));
  }

  private exportIndividualPdf(
    workspaceId: string,
    sectionsVisibilitySettings: TSectionVisibilitySettings,
    exportData: TExportData,
    body: TExportBody,
  ): Observable<Response> {
    this.pointExportPreferencesService.updatePreferences({
      workspaceId,
      exportType: 'pdf',
      sectionsVisibilitySettings,
      format: exportData.format,
    });

    return this.exportFromUrl(API_export_pdf_individual(), body);
  }

  private addPointsToFiltersRequest(
    filters: {
      groupOperator: TSearchAdvancedFilterOperator;
      groups: TSearchFilterSubgroup[];
      criteria: TSearchFilterDTO[];
    },
    pointsToAdd: TSearchFilterDTO,
  ): {
    groupOperator: TSearchAdvancedFilterOperator;
    groups: TSearchFilterSubgroup[];
    criteria: TSearchFilterDTO[];
  } {
    const [firstGroup, ...otherGroups] = filters.groups ?? [];
    const [firstSubgroup, ...otherSubgroups] = firstGroup?.subgroups ?? [];

    return {
      ...filters,
      groups: [
        {
          ...firstGroup,
          subgroups: [
            {
              ...firstSubgroup,
              criteria: [...(firstSubgroup?.criteria ?? []), pointsToAdd],
            },
            ...otherSubgroups,
          ],
        },
        ...otherGroups,
      ],
    };
  }

  private getWorkspaceIdsAllowedByFilters(): string[] {
    const allWorkspaceIds = this.workspaceService.getAllWorkspaceIds();
    const filters = this.quickFilterService.getFilters();
    const accountSiteRows = [...filters.quick, ...filters.advanced].filter((row) =>
      row.keys?.some((key) => key.toLowerCase() === 'workspaceref.id'),
    );

    if (!accountSiteRows.length) {
      return allWorkspaceIds;
    }

    let matchingWorkspaceIds: string[] = allWorkspaceIds;

    accountSiteRows.forEach((row) => {
      const values: string[] = (row.values ?? []).filter(
        (value): value is string => typeof value === 'string',
      );

      if (row.operator === EPointSearchOperator.IN) {
        matchingWorkspaceIds = matchingWorkspaceIds.filter((id) => values.includes(id));
      } else if (row.operator === EPointSearchOperator.NOT_IN) {
        matchingWorkspaceIds = matchingWorkspaceIds.filter((id) => !values.includes(id));
      }
    });

    return matchingWorkspaceIds;
  }

  private formatWithFilters(body: TExportBody): TExportBodyRefactored {
    const table = GET_TABLE();
    let filters = this.quickFilterService.getFiltersTransformed();
    let elementsPerWorkspace = [];

    if (table.hasSelectedAllPoints && table.deselectedPoints.size === 0) {
      const workspaceIds = this.getWorkspaceIdsAllowedByFilters();

      elementsPerWorkspace = workspaceIds.map((workspaceId: string) => ({
        workspaceId,
        pointIds: [],
      }));
    } else if (table.selectedPointsCount > 0 && table.deselectedPoints.size === 0) {
      elementsPerWorkspace = body.elementsPerWorkspace.map((elements: TElementPerWorkspace) => ({
        workspaceId: elements.workspaceId,
        pointIds: [],
      }));

      const includedPoints = {
        values: [...table.selectedPoints],
        operator: EPointSearchOperator.IN,
        key: '_id',
      };

      filters = this.addPointsToFiltersRequest(filters, includedPoints);
    } else {
      const workspaceIds = this.getWorkspaceIdsAllowedByFilters();
      const allWorkspaces = this.workspaceService.getWorkspaces();

      const deselectedPointsArray = [...table.deselectedPoints];
      const deselectedPointsWorkspaces = deselectedPointsArray.map((pointId) => {
        const match = Object.values(allWorkspaces).find((workspace) =>
          workspace?.pointIds?.includes(pointId),
        );

        return match ? match.workspaceId : null;
      });

      const workspaces = workspaceIds.filter((item) => !deselectedPointsWorkspaces.includes(item));
      elementsPerWorkspace = workspaces.map((workspaceId) => ({
        workspaceId,
        pointIds: [],
      }));

      const excludedPoints = {
        values: deselectedPointsArray,
        operator: EPointSearchOperator.NOT_IN,
        key: '_id',
      };

      filters = this.addPointsToFiltersRequest(filters, excludedPoints);
    }

    return {
      filter: filters,
      sort: this.quickSortService.getSortTransformed(),
      timeGMT: body.timeGMT,
      format: body.format,
      elementsPerWorkspace,
      sectionsVisibilitySettings: body.sectionsVisibilitySettings,
      exportFilter: body.exportFilter,
    };
  }

  private exportDetailedList(
    workspaceId: string,
    sectionsVisibilitySettings: TSectionVisibilitySettings,
    exportData: TExportData,
    body: TExportBody,
  ): Observable<Response> {
    this.pointExportPreferencesService.updatePreferences({
      workspaceId,
      exportType: 'pdf',
      sectionsVisibilitySettings,
      format: exportData.format,
    });

    const exportBody: TExportBody = {
      ...body,
      filter: this.quickFilterService.getFiltersTransformed(),
    };

    return this.exportFromUrl(API_export_pdf_detailed(), exportBody);
  }

  private exportOverviewDetailedList(
    sectionsVisibilitySettings: TSectionVisibilitySettings,
    body: TExportBody,
  ): Observable<Response> {
    const preferences = this.preferencesService.getPreferences();

    preferences.overviewExports.pdf = sectionsVisibilitySettings;

    this.pointExportPreferencesService.updateOverviewPreferences({
      exportType: 'pdf',
      sectionsVisibilitySettings: body.sectionsVisibilitySettings,
      format: body.format,
    });

    return this.exportFromUrl(API_export_pdf_detailed_overview(), this.formatWithFilters(body));
  }

  private showPermissionDeniedWarninig(): void {
    const prompt = this.translationPipe.transform('export_permission_denied');

    this.promptService.showWarning(prompt);
  }

  private exportFromUrl(
    url: string,
    body?: TExportBody | TExportBodyRefactored,
  ): Observable<Response> {
    return this.downloadService.fromRequest(url, body).pipe(
      tap((response) => {
        if (response.status === EStatusCode.ACCEPTED) {
          const promptText = this.translationPipe.transform(
            'prompt_export_will_shortly_receive_a_link_via_email',
          );

          this.promptService.showSuccess(promptText, { duration: 15 });
        }
      }),
    );
  }
}
